import { inject, Injectable, signal } from '@angular/core';
import { PrimeNG } from 'primeng/config';
import { 
  SlatePreset, 
  OrangePreset, 
  BluePreset, 
  RedPreset, 
  EmeraldPreset, 
  PurplePreset,
  TealPreset,
  PinkPreset,
  IndigoPreset,
  CyanPreset,
  YellowPreset,
  RosePreset,
  FuchsiaPreset,
  VioletPreset,
  LimePreset,
  AmberPreset,
  SkyPreset,
  StonePreset,
  NeutralPreset,
  ZincPreset,
  GrayPreset
} from '../../themes/triventa-theme';

export type ThemePreset = 
  | 'slate' 
  | 'orange' 
  | 'blue' 
  | 'red' 
  | 'emerald' 
  | 'purple'
  | 'teal'
  | 'pink'
  | 'indigo'
  | 'cyan'
  | 'yellow'
  | 'rose'
  | 'fuchsia'
  | 'violet'
  | 'lime'
  | 'amber'
  | 'sky'
  | 'stone'
  | 'neutral'
  | 'zinc'
  | 'gray';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentTheme = signal<ThemePreset>('teal');
  private primeng = inject(PrimeNG);
  
  private presets = {
    slate: SlatePreset,
    orange: OrangePreset,
    blue: BluePreset,
    red: RedPreset,
    emerald: EmeraldPreset,
    purple: PurplePreset,
    teal: TealPreset,
    pink: PinkPreset,
    indigo: IndigoPreset,
    cyan: CyanPreset,
    yellow: YellowPreset,
    rose: RosePreset,
    fuchsia: FuchsiaPreset,
    violet: VioletPreset,
    lime: LimePreset,
    amber: AmberPreset,
    sky: SkyPreset,
    stone: StonePreset,
    neutral: NeutralPreset,
    zinc: ZincPreset,
    gray: GrayPreset
  };

  constructor() {
    const defaultTheme: ThemePreset = 'teal';
    
    // Initialize with teal theme
    this.currentTheme.set(defaultTheme);
    this.applyTheme(defaultTheme);
    this.updateCSSVariables(defaultTheme);
    
    // Save teal as the new default
    localStorage.setItem('theme', defaultTheme);
  }

  getCurrentTheme() {
    return this.currentTheme.asReadonly();
  }

  setTheme(theme: ThemePreset) {
    if (this.presets[theme]) {
      this.currentTheme.set(theme);
      this.applyTheme(theme);
      this.updateCSSVariables(theme);
      localStorage.setItem('theme', theme);
    }
  }

  private applyTheme(theme: ThemePreset) {
    this.primeng.theme.set({
      preset: this.presets[theme],
      options: {
        darkModeSelector: '.my-app-dark',
        cssLayer: {
          name: 'primeng',
          order: 'tailwind, primeng'
        }
      }
    });
  }

  private updateCSSVariables(theme: ThemePreset) {
    const preset = this.presets[theme] as any;
    const lightColors = preset.semantic?.colorScheme?.light;
    const darkColors = preset.semantic?.colorScheme?.dark;

    if (!lightColors || !darkColors) return;

    // Update CSS custom properties for Tailwind
    const root = document.documentElement;
    
    // Light mode colors
    if (lightColors.surface) {
      Object.entries(lightColors.surface).forEach(([key, value]) => {
        root.style.setProperty(`--surface-${key}`, value as string);
      });
    }
    
    if (lightColors.primary) {
      Object.entries(lightColors.primary).forEach(([key, value]) => {
        root.style.setProperty(`--primary-${key}`, value as string);
      });
    }

    // Dark mode colors (will be applied when .my-app-dark class is present)
    const darkModeStyles = document.getElementById('dark-mode-styles') || document.createElement('style');
    darkModeStyles.id = 'dark-mode-styles';
    
    let darkCSS = '.my-app-dark {';
    if (darkColors.surface) {
      Object.entries(darkColors.surface).forEach(([key, value]) => {
        darkCSS += `--surface-${key}: ${value};`;
      });
    }
    if (darkColors.primary) {
      Object.entries(darkColors.primary).forEach(([key, value]) => {
        darkCSS += `--primary-${key}: ${value};`;
      });
    }
    darkCSS += '}';
    
    darkModeStyles.textContent = darkCSS;
    
    if (!document.head.contains(darkModeStyles)) {
      document.head.appendChild(darkModeStyles);
    }
  }

  getAvailableThemes(): ThemePreset[] {
    return Object.keys(this.presets) as ThemePreset[];
  }

  getThemeOptions() {
    return [
      { label: 'Slate', value: 'slate' as ThemePreset, color: '#64748b' },
      { label: 'Orange', value: 'orange' as ThemePreset, color: '#f97316' },
      { label: 'Blue', value: 'blue' as ThemePreset, color: '#3b82f6' },
      { label: 'Red', value: 'red' as ThemePreset, color: '#ef4444' },
      { label: 'Emerald', value: 'emerald' as ThemePreset, color: '#10b981' },
      { label: 'Purple', value: 'purple' as ThemePreset, color: '#a855f7' },
      { label: 'Teal', value: 'teal' as ThemePreset, color: '#14b8a6' },
      { label: 'Pink', value: 'pink' as ThemePreset, color: '#ec4899' },
      { label: 'Indigo', value: 'indigo' as ThemePreset, color: '#6366f1' },
      { label: 'Cyan', value: 'cyan' as ThemePreset, color: '#06b6d4' },
      { label: 'Yellow', value: 'yellow' as ThemePreset, color: '#eab308' },
      { label: 'Rose', value: 'rose' as ThemePreset, color: '#f43f5e' },
      { label: 'Fuchsia', value: 'fuchsia' as ThemePreset, color: '#d946ef' },
      { label: 'Violet', value: 'violet' as ThemePreset, color: '#8b5cf6' },
      { label: 'Lime', value: 'lime' as ThemePreset, color: '#65a30d' },
      { label: 'Amber', value: 'amber' as ThemePreset, color: '#f59e0b' },
      { label: 'Sky', value: 'sky' as ThemePreset, color: '#0ea5e9' },
      { label: 'Stone', value: 'stone' as ThemePreset, color: '#78716c' },
    ];
  }
}
